<template>
  <div>
    <page-header
      :page="page"
      :actions="headerActionConfig"
      left-side-class="col-xl-8"
      right-side-class="col-xl-4 d-flex align-items-end justify-content-end pb-2"
    />
    <div class="row mx-0 mt-4">
      <div class="col-5">
        <student-card
          title="SIE del Estudiante a Permanecer"
          :student="model.studentToKeep"
          :can-remove="!studentsReadOnly"
          @search-student="searchStudentHandler($event, false)"
          @remove-student="removeStudentHandler(false)"
          @invert-student="invertStudentHandler($event)"
        />
      </div>
      <div class="col-2 d-flex align-items-center justify-content-center">
        <em
          class="fas fa-arrow-alt-circle-right arrow-icon"
          :class="
            model.studentToKeep === null || model.studentToDelete === null
              ? 'color-gray-5'
              : 'color-green--1'
          "
        />
      </div>
      <div class="col-5">
        <student-card
          title="SIE del Estudiante a Eliminar"
          :student="model.studentToDelete"
          :can-remove="!studentsReadOnly"
          :errorMessage="model.errorMessage"
          @search-student="searchStudentHandler($event, true)"
          @remove-student="removeStudentHandler(true)"
          @invert-student="invertStudentHandler($event)"
        />
      </div>
    </div>
    <div v-if="showError" class="row mx-0 mt-4">
      <div class="col-12">
        <custom-error
          title="Aviso"
          description-height="150px"
          :errors="errorMessages"
          @close="showError = false"
        />
      </div>
    </div>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import StudentCard from '@/views/student/sieReplacement/components/StudentCard.vue';
import {
  getStudent,
  replacementSieValidate,
  replacementSie,
  invertSie,
  hasSieReplacement,
  deleteSieReplacement,
} from '@/modules/student/api.js';
import alert from 'utils/mixins/alert.mixin.js';
import CustomError from '@/components/common/CustomError';
import DocumentsService from '@/services/report';
import sieReplacementCategories from '@/utils/constants/student/sieReplacementCategories';

export default {
  name: 'SieReplacement',
  mixins: [alert],
  components: {
    PageHeader,
    StudentCard,
    CustomError,
  },
  data: () => ({
    page: {
      icon: 'fas fa-user-circle',
      title: 'Reemplazo de SIE',
      routes: ['Inicio', 'Estudiante', 'Reemplazo de SIE'],
    },
    model: {
      studentToKeep: null,
      studentToDelete: null,
    },
    showError: false,
    errorMessages: [],
    studentsReadOnly: false,
    replacementSuccess: false,
    hasReplacement: null,
  }),
  computed: {
    headerActionConfig() {
      return [
        {
          text: 'Regresar',
          icon: 'fas fa-arrow-circle-left',
          variant: 'outline-success',
          click: () => this.backHandler(),
        },
        {
          text: 'Continuar',
          icon: 'fas fa-arrow-alt-circle-right',
          variant: 'success',
          visible:
            this.model.studentToKeep !== null &&
            this.model.studentToKeep.fullName !== null &&
            this.model.studentToDelete !== null &&
            this.model.studentToDelete.fullName !== null &&
            !this.studentsReadOnly,
          click: () => this.continueHandler(),
        },
        {
          text: 'Procesar',
          icon: 'fas fa-redo-alt',
          variant: 'success',
          visible: this.studentsReadOnly && !this.replacementSuccess,
          click: () => this.processHandler(),
        },
        {
          text: 'Eliminar Reemplazo',
          icon: 'fas fa-times-circle',
          variant: 'warning',
          visible: this.canDeleteReplacement,
          click: () => this.deleteReplacement(),
        },
        {
          text: 'Imprimir',
          icon: 'fas fa-print',
          variant: 'outline-success',
          click: () => this.print(),
          visible:
            this.studentsReadOnly &&
            this.hasDownloadPermission &&
            this.replacementSuccess,
        },
      ];
    },
    hasDownloadPermission() {
      return this.userHasPermissions('eduesp.student.replacesie.download');
    },
    hasDeleteReplacementPermission() {
      return this.userHasPermissions(
        'student.siereplacement.canremovereplacement'
      );
    },
    canDeleteReplacement() {
      return (
        this.hasDeleteReplacementPermission &&
        this.studentsReadOnly &&
        this.hasReplacement == true
      );
    },
  },
  methods: {
    async searchStudentHandler(sie, isFromDelete) {
      //Avoid searching the same student twice
      const { data } = await getStudent(sie);
      const studentIsSameMessage =
        'El estudiante a eliminar no puede ser el mismo que el estudiante a permanecer';
      let errorMessage = '';
      if (isFromDelete) {
        if (
          this.model.studentToKeep !== undefined &&
          this.model.studentToKeep !== null &&
          this.model.studentToKeep.sie !== undefined &&
          this.model.studentToKeep.sie === sie
        ) {
          errorMessage = studentIsSameMessage;
        } else if (
          data.isPublic !== undefined &&
          data.isPublic !== null &&
          data.isPublic
        ) {
          errorMessage =
            'El número de SIE ingresado pertenece a un estudiante ' +
            (data.isPublic ? 'público' : 'privado') +
            ' quién todavía cuenta con matrícula activa.';
        }
        this.model.studentToDelete = { ...data, sie, errorMessage };
      } else {
        if (
          this.model.studentToDelete !== undefined &&
          this.model.studentToDelete !== null &&
          this.model.studentToDelete.sie !== undefined &&
          this.model.studentToDelete.sie === sie
        ) {
          errorMessage = studentIsSameMessage;
        }
        this.model.studentToKeep = { ...data, sie, errorMessage };
      }
    },
    removeStudentHandler(isFromDelete) {
      this.showError = false;
      if (isFromDelete) {
        this.model.studentToDelete = null;
        return;
      }
      this.model.studentToKeep = null;
    },
    backHandler() {
      if (this.studentsReadOnly && !this.replacementSuccess) {
        this.studentsReadOnly = false;
        this.showError = false;
      } else {
        this.$router.go(-1);
      }
    },
    async continueHandler() {
      // We looking for for conflicts between students
      const { data } = await replacementSieValidate(
        this.model.studentToKeep.sie,
        this.model.studentToDelete.sie
      );

      if (data && data.length > 0) {
        this.showError = true;
        this.errorMessages = [];

        let tables = [...new Set(data.map((o) => o.tableName))].join(', ');
        let errorMessage = `El estudiante a eliminar cuenta con los siguientes procesos activos en sistema: ${tables}. Para poder proceder con el reemplazo de SIE, deberá realizar los procesos correspondientes en sistema.`;
        await this.Toast('', `${errorMessage}`, 'error', 10000);

        const groupByCategory = data.reduce((group, item) => {
          const { category } = item;
          group[category] = group[category] ?? [];
          group[category].push(item);
          return group;
        }, {});

        let map = new Map();
        for (let category in groupByCategory) {
          if (category === sieReplacementCategories.PEI) {
            let peiNumber = groupByCategory[category][0].referenceNumber;
            await this.Toast(
              '',
              `El estudiante cuenta con un PEI [${peiNumber}]. Favor de cancelar el PEI por pantalla.`,
              'error',
              10000
            );
          } else {
            if (category === sieReplacementCategories.Register) {
              let registerStatus = groupByCategory[category][0].referenceStatus;
              await this.Toast(
                '',
                `El estudiante cuenta con un registro en estatus [${registerStatus}]. Favor inactivar el registro por pantalla.`,
                'error',
                10000
              );
            } else {
              groupByCategory[category].forEach((x) => {
                const key = `${category}${x.referenceStatus}`;
                if (!map.has(key)) {
                  map.set(key, {
                    category: x.category,
                    status: x.referenceStatus,
                    count: 1,
                  });
                } else {
                  map.get(key).count++;
                }
              });
            }
          }
        }

        for (let value of map.values()) {
          await this.Toast(
            '',
            `El estudiante cuenta con (${value.count}) ${value.category}(s) en estatus [${value.status}].`,
            'error',
            10000
          );
        }

        data.forEach((validation) => {
          let message = `<strong>Tabla:</strong> ${validation.tableName};`;
          message += validation.referenceNumber
            ? ` <strong>Número de Referencia:</strong> ${validation.referenceNumber};`
            : '';
          message += validation.referenceStatus
            ? ` <strong>Estatus de Referencia:</strong> ${validation.referenceStatus};`
            : '';
          message += validation.comments
            ? ` <strong>Comentarios:</strong> ${validation.comments};`
            : '';
          this.errorMessages.push(message);
        });
      } else {
        this.studentsReadOnly = true;
        // check if has permission and has remplacement already
        if (this.hasDeleteReplacementPermission) {
          const { data: hasReplacement } = await hasSieReplacement(
            this.model.studentToKeep.sie
          );
          if (hasReplacement) {
            this.hasReplacement = hasReplacement;
          }
        }
      }
    },
    dispose() {
      this.model.studentToKeep = null;
      this.model.studentToDelete = null;
      this.showError = false;
      this.errorMessages = [];
      this.studentsReadOnly = false;
      this.replacementSuccess = false;
    },
    async processHandler() {
      let response = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea realizar el proceso de reemplazo de SIE <b>${
          this.model.studentToDelete.sie
        }</b> al SIE <b>${this.model.studentToKeep.sie}</b> ${
          this.errorMessages.length > 0 ? 'con datos existentes' : ''
        }?`,
        'Sí',
        'No'
      );
      if (response) {
        let message = '';
        await replacementSie(
          this.model.studentToKeep.sie,
          this.model.studentToDelete.sie
        )
          .then((res) => {
            if (!res.data.result) {
              message =
                res?.data?.message ||
                'Error al procesar el reemplazo de SIE. Validar las relaciones reportadas.';
              this.ShowToast('', `${message}`, 'error');
            } else {
              this.ShowToast(
                '',
                'El reemplazo de SIE se realizó exitosamente.',
                'success'
              );
              this.replacementSuccess = true;
            }
          })
          .catch((err) => {
            message = err.response?.data?.message || err.message;
            this.ShowToast('', `${message}`, 'error');
          });
      }
    },
    async deleteReplacement() {
      let response = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea eliminar el reemplazo de SIE?`,
        'Sí',
        'No'
      );

      if (response) {
        await deleteSieReplacement(this.model.studentToKeep.sie);
        this.dispose();
        this.ShowToast(
          '',
          'El reemplazo de SIE ha sido reestablecido exitosamente.',
          'success'
        );

        this.hasReplacement = null;
      }
    },
    async invertStudentHandler(studentSie) {
      let response = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro que desea invertir el reemplazo de SIE?`,
        'Sí',
        'No'
      );

      if (response) {
        await invertSie(studentSie);
        this.dispose();
        this.ShowToast(
          '',
          'El SIE ha sido reestablecido exitosamente.',
          'success'
        );

        this.hasReplacement = null;
      }
    },

    async print() {
      DocumentsService.downloadSieReplace(this.model.studentToDelete.sie);
    },
  },
};
</script>

<style lang="scss" scoped>
.arrow-icon {
  font-size: 5.5rem;
}
</style>
